import { PHASE, PLAYER, POSITION_COUNTS } from "../../../common/constants.ts";
import {
	contractNegotiation,
	draft,
	league,
	player,
	team,
	freeAgents,
} from "../index.ts";
import { idb } from "../../db/index.ts";
import { g, helpers, local, logEvent } from "../../util/index.ts";
import type { Conditions, PhaseReturn } from "../../../common/types.ts";
import { last, orderBy } from "../../../common/utils.ts";
import { getNumPlayersTradedAwayNormalizedAll } from "../player/getNumPlayersTradedAwayNormalized.ts";
import { bySport, isSport } from "../../../common/sportFunctions.ts";
import {
	controlSalary,
	controlSeasonsUsed,
	minorLeagueFreeAgency,
} from "../player/minorsLifecycle.baseball.ts";
import { ValueChangeCalculator } from "../team/ValueChangeCalculator.ts";
import {
	isMaxContract,
	roomForMaxContract,
} from "../team/maxContracts.baseball.ts";

export const FREE_AGENCY_DAYS = 30;

const newPhaseResignPlayers = async (
	conditions: Conditions,
): Promise<PhaseReturn> => {
	// In case some weird situation results in games still in the schedule, clear them
	await idb.cache.schedule.clear();

	// Clear any negotiations that still somehow exist, except if it's a re-signing negotiation for the user, because that could be from a prior failed attempt to run this function and we want to keep those guys. (Would rather have phase updates be transactional, but oh well.)
	const existingNegotiations = await idb.cache.negotiations.getAll();
	const userTids = g.get("userTids");
	for (const negotiation of existingNegotiations) {
		if (negotiation.resigning && userTids.includes(negotiation.tid)) {
			continue;
		}

		await idb.cache.negotiations.delete(negotiation.pid);
	}

	const repeatSeasonType = g.get("repeatSeason")?.type;

	// Reset contract demands of current free agents and undrafted players
	// KeyRange only works because PLAYER.UNDRAFTED is -2 and PLAYER.FREE_AGENT is -1
	const existingFreeAgents = await idb.cache.players.indexGetAll(
		"playersByTid",
		PLAYER.FREE_AGENT,
	);
	const undraftedPlayers =
		!repeatSeasonType && !g.get("forceHistoricalRosters")
			? (
					await idb.cache.players.indexGetAll("playersByDraftYearRetiredYear", [
						[g.get("season")],
						[g.get("season"), Infinity],
					])
				).filter((p) => p.tid === PLAYER.UNDRAFTED)
			: [];

	const numPlayersTradedAwayNormalized =
		await getNumPlayersTradedAwayNormalizedAll();
	for (const p of [...existingFreeAgents, ...undraftedPlayers]) {
		player.addToFreeAgents(p, numPlayersTradedAwayNormalized);
		await idb.cache.players.put(p);
	}

	// Veterans of the farm system who aren't prospects leave first (minor league free agency)
	if (isSport("baseball")) {
		await minorLeagueFreeAgency(conditions);
	}

	// Minor league contracts expire like any other. Otherwise a player sent down would be
	// held forever without ever reaching free agency. He returns to his club for the normal
	// re-signing decision: the user negotiates, the AI decides, and if not re-signed he
	// becomes a free agent. A club can send a re-signed player back down afterwards.
	const backFromMinors = new Set<number>();
	for (const p of await idb.cache.players.indexGetAll(
		"playersByTid",
		PLAYER.MINORS,
	)) {
		if (p.contract.exp <= g.get("season") && p.minorTid !== undefined) {
			p.tid = p.minorTid;
			delete p.minorTid;
			backFromMinors.add(p.pid);
			await idb.cache.players.put(p);
		}
	}

	// Re-sign players on user's team, and some AI players
	const players = await idb.cache.players.indexGetAll("playersByTid", [
		0,
		Infinity,
	]);

	// Figure out how many players are needed at each position, beyond who is already signed
	type PositionInfo = Record<
		string,
		{
			count: number;
			maxValue: number;
		}
	>;
	const positionInfoByTid = new Map<number, PositionInfo>();

	if (Object.keys(POSITION_COUNTS).length > 0) {
		for (let tid = 0; tid < g.get("numTeams"); tid++) {
			const positionInfo: PositionInfo = {};
			for (const [pos, count] of Object.entries(POSITION_COUNTS)) {
				positionInfo[pos] = {
					count,
					maxValue: 0,
				};
			}
			positionInfoByTid.set(tid, positionInfo);
		}

		for (const p of players) {
			// Only expiring contracts and hard cap rookies!
			if (p.contract.exp <= g.get("season")) {
				continue;
			}

			const positionInfo = positionInfoByTid.get(p.tid);
			const pos = last(p.ratings).pos;

			if (positionInfo !== undefined && positionInfo[pos] !== undefined) {
				positionInfo[pos].count -= 1;
				if (p.value > positionInfo[pos].maxValue) {
					positionInfo[pos].maxValue = p.value;
				}
			}
		}
	}

	const payrollsByTid = new Map<number, number>();

	if (g.get("salaryCapType") === "hard") {
		for (let tid = 0; tid < g.get("numTeams"); tid++) {
			const payroll = await team.getPayroll(tid);
			const expiringPayroll = players
				.filter((p) => p.tid === tid && p.contract.exp <= g.get("season"))
				.reduce((total, p) => total + p.contract.amount, 0);
			payrollsByTid.set(tid, payroll - expiringPayroll);
		}
	}

	const expiringPids = orderBy(
		players.filter((p) => p.contract.exp <= g.get("season")),
		[
			"tid",
			(p) => {
				return p.draft.year === g.get("season") ? 1 : -1;
			},
			"value",
		],
		["asc", "desc", "desc"],
	).map((p) => p.pid);

	const expiredRookieContractPids = new Set(
		players
			.filter(
				(p) =>
					p.contract.exp <= g.get("season") &&
					p.contract.rookie &&
					p.draft.year < g.get("season"),
			)
			.map((p) => p.pid),
	);

	await freeAgents.normalizeContractDemands({
		type: "includeExpiringContracts",
	});

	await contractNegotiation.cancelAll();

	const valueChangeCalculator = new ValueChangeCalculator();
	for (const pid of expiringPids) {
		// Re-fetch players, because normalizeContractDemands might have changed some objects
		const p = await idb.cache.players.get(pid);
		if (!p) {
			continue;
		}

		if (expiredRookieContractPids.has(p.pid)) {
			p.contract.rookieResign = true;
		}

		const draftPick = p.draft.year === g.get("season");

		// Still under team control: renewed for one year at the minimum or the arbitration salary, no negotiation.
		// The AI lets an arbitration-eligible player go (non-tender) when the team is better off without him.
		const controlUsed =
			isSport("baseball") && !draftPick
				? controlSeasonsUsed(p, g.get("season"))
				: undefined;
		if (controlUsed !== undefined) {
			const contract = {
				amount: controlSalary(controlUsed, p.contract.amount),
				exp: g.get("season") + 1,
			};
			const userTeam =
				g.get("userTids").includes(p.tid) &&
				!local.autoPlayUntil &&
				!g.get("spectator");
			const keep =
				userTeam ||
				controlUsed < 3 ||
				(await valueChangeCalculator.evaluate({
					tid: p.tid,
					pidsAdd: [],
					pidsRemove: [p.pid],
					dpidsAdd: [],
					dpidsRemove: [],
					tradingPartnerTid: undefined,
				})) < 0;
			if (keep) {
				await player.sign(p, p.tid, contract, PHASE.RESIGN_PLAYERS);
				if (backFromMinors.has(p.pid)) {
					p.minorTid = p.tid;
					p.tid = PLAYER.MINORS;
				}
				await valueChangeCalculator.invalidateCache({ teams: [p.minorTid ?? p.tid] });
			} else {
				player.addToFreeAgents(p, numPlayersTradedAwayNormalized);
			}
			delete p.contract.rookieResign;
			await idb.cache.players.put(p);
			continue;
		}

		if (draftPick && !g.get("draftPickAutoContract")) {
			p.contract.amount /= 2;

			if (p.contract.amount < g.get("minContract")) {
				p.contract.amount = g.get("minContract");
			} else {
				p.contract.amount = helpers.roundContract(p.contract.amount);
			}

			p.contract.rookie = true;
		}

		if (
			g.get("userTids").includes(p.tid) &&
			!local.autoPlayUntil &&
			!g.get("spectator")
		) {
			const tid = p.tid;

			player.addToFreeAgents(p, numPlayersTradedAwayNormalized);

			await idb.cache.players.put(p);
			const info = await contractNegotiation.create(p.pid, true, tid);

			if (typeof info === "string") {
				logEvent(
					{
						type: "refuseToSign",
						text: info,
						pids: [p.pid],
						tids: [tid],
					},
					conditions,
				);
			} else {
				await idb.cache.negotiations.add(info);
			}
		} else {
			let reSignPlayer = true;

			const contract = {
				...p.contract,
			};
			const payroll = payrollsByTid.get(p.tid);

			const positionInfo = positionInfoByTid.get(p.tid);
			const pos = last(p.ratings).pos;

			if (g.get("salaryCapType") === "hard") {
				if (payroll === undefined) {
					throw new Error(
						"Payroll should always be defined if there is a hard cap",
					);
				}
				if (contract.amount + payroll > g.get("salaryCap")) {
					reSignPlayer = false;
				}

				// Don't go beyond roster needs by position
				if (
					bySport({
						baseball: true,
						basketball: false,
						football: true,
						hockey: true,
					}) &&
					positionInfo !== undefined &&
					positionInfo[pos] !== undefined &&
					positionInfo[pos].count <= 0 &&
					positionInfo[pos].maxValue > p.value
				) {
					reSignPlayer = false;
				}

				// Always sign rookies
				if (draftPick) {
					reSignPlayer = true;
				}
			}

			if (reSignPlayer) {
				const mood = await player.moodInfo(p, p.tid, {
					contractAmount: p.contract.amount,
				});

				// A player worth the maximum stays for what he asks this club, discounts included; a
				// money-driven one asking the maximum leaves if the club already has five
				if (isMaxContract(p.contract.amount)) {
					contract.amount = mood.contractAmount;
				}
				if (
					!roomForMaxContract(
						await idb.cache.players.indexGetAll("playersByTid", p.tid),
						contract.amount,
						p.pid,
					)
				) {
					reSignPlayer = false;
				}

				// Player must be willing to sign (includes draft picks and first year after expansion, from moodInfo)
				if (!mood.willing || !reSignPlayer) {
					reSignPlayer = false;
				} else {
					// Is team better off without him?
					const dv = await valueChangeCalculator.evaluate({
						tid: p.tid,
						pidsAdd: [],
						pidsRemove: [p.pid],
						dpidsAdd: [],
						dpidsRemove: [],
						tradingPartnerTid: undefined,
					});

					// Skip re-signing some low value players, otherwise teams fill up their rosters too readily
					const skipBadPlayer =
						contract.amount < g.get("minContract") * 2 && Math.random() < 0.5;

					// More randomness if hard cap
					const whatever =
						g.get("salaryCapType") === "hard" ? Math.random() > 0.1 : true;

					if (
						draftPick ||
						(mood.willing && dv < 0 && !skipBadPlayer && whatever)
					) {
						await player.sign(p, p.tid, contract, PHASE.RESIGN_PLAYERS);

						if (positionInfo !== undefined && positionInfo[pos] !== undefined) {
							positionInfo[pos].count -= 1;
							if (p.value > positionInfo[pos].maxValue) {
								positionInfo[pos].maxValue = p.value;
							}
						}

						if (payroll !== undefined) {
							payrollsByTid.set(p.tid, contract.amount + payroll);
						}

						// Need to recompute team value stuff now that a player was signed
						await valueChangeCalculator.invalidateCache({
							teams: [p.tid],
						});
					} else {
						reSignPlayer = false;
					}
				}
			}

			if (!reSignPlayer) {
				player.addToFreeAgents(p, numPlayersTradedAwayNormalized);
			}

			// Delete rookieResign for AI players, since we're done re-signing them. Leave it for user players.
			if (expiredRookieContractPids.has(pid) || p.contract.rookieResign) {
				delete p.contract.rookieResign;
			}

			await idb.cache.players.put(p);
		}
	}

	const draftProspects = await idb.cache.players.indexGetAll(
		"playersByTid",
		PLAYER.UNDRAFTED,
	);

	if (repeatSeasonType === "players") {
		// Bump up age of draft prospects, so they stay the same
		for (const p of draftProspects) {
			p.draft.year += 1;
			p.born.year += 1;
			last(p.ratings).season += 1;
			await player.updateValues(p);
			await idb.cache.players.put(p);
		}
	} else {
		// Bump up future draft classes (not simultaneous so tid updates don't cause race conditions)
		for (const p of draftProspects) {
			if (p.draft.year !== g.get("season") + 1) {
				continue;
			}

			p.ratings[0].fuzz /= Math.sqrt(2);
			await player.develop(p, 0); // Update skills/pot based on fuzz

			await player.updateValues(p);
			await idb.cache.players.put(p);
		}

		for (const p of draftProspects) {
			if (p.draft.year !== g.get("season") + 2) {
				continue;
			}

			p.ratings[0].fuzz /= Math.sqrt(2);
			await player.develop(p, 0); // Update skills/pot based on fuzz

			await player.updateValues(p);
			await idb.cache.players.put(p);
		}

		// Generate a new draft class, while leaving existing players in that draft class in place
		await draft.genPlayers(g.get("season") + 3);
	}

	// Delete any old undrafted players that still somehow exist
	const toRemove = [];
	for (const p of draftProspects) {
		if (p.draft.year <= g.get("season")) {
			toRemove.push(p.pid);
		}
	}
	await player.remove(toRemove);

	// Set daysLeft here because this is "basically" free agency, so some functions based on daysLeft need to treat it that way (such as the trade AI being more reluctant)
	await league.setGameAttributes({
		daysLeft: FREE_AGENCY_DAYS,
	});

	return {
		redirect: {
			url: helpers.leagueUrl(["negotiation"]),
			text: "Re-sign players",
		},
		updateEvents: ["playerMovement"],
	};
};

export default newPhaseResignPlayers;
